import {
  useFindAllCompaniesQuery,
  useFindOneUserQuery,
  useRemoveUserMutation,
} from '../../api/api-hooks';
import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { Autocomplete } from '@mui/material';
import { UserFormFromScratch } from './userTypes';
import useUserItemUpdater from './useUserItemUpdater';
import { userMiddleware, userValidatorFromScratch } from './userUtils';
import DeleteItemDialog from '../../components/DeleteItemDialog';
import EditItemDialog from '../../components/EditItemDialog';

interface Props {
  selectedUsers: Item[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const UsersOptionsBar = ({
  selectedUsers,
  setSelectedUsers,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteUser] = useRemoveUserMutation();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const userUpdater = useUserItemUpdater();
  const { data: companies } = useFindAllCompaniesQuery({});
  if (companies === undefined) return null;
  const handleEditModalOpen = () => {
    setEditModalOpenFlag(true);
  };
  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true);
  };
  return (
    <>
      <Grid
        container
        justifyContent='flex-start'
        alignItems='center'
        spacing={1}
        style={{ marginBottom: '.5rem' }}
      >
        {selectedUsers.length === 1 && (
          <Grid item>
            <Button
              onClick={() => handleEditModalOpen()}
              variant='contained'
              color='primary'
            >
              Edit
            </Button>
          </Grid>
        )}
        {selectedUsers.length > 0 && (
          <Grid item>
            <Button
              onClick={() => handleDeleteModalOpen()}
              variant='contained'
              color='primary'
            >
              Delete
            </Button>
          </Grid>
        )}
      </Grid>
      {selectedUsers.length === 1 && (
        <EditItemDialog
          editModalOpen={editModalOpenFlag}
          setEditModalOpen={setEditModalOpenFlag}
          itemGetter={useFindOneUserQuery}
          itemId={selectedUsers[0].id}
          itemUpdater={userUpdater}
          children={(addFormState: UserFormFromScratch, handler: any) => (
            <Grid container spacing={4}>
              <Grid container xs={6} item direction='column' spacing={1}>
                <Grid item>
                  <Autocomplete
                    onChange={(event, value) => {
                      handler(event, value, 'company');
                    }}
                    value={addFormState.company}
                    options={companies}
                    getOptionLabel={(company) => company.companyName}
                    style={{ width: 300, marginTop: '.5rem' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Company'
                        name='company'
                        variant='outlined'
                        id='company'
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='name'
                    label='Name'
                    fullWidth
                    value={nullStringCheck(addFormState.name)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='email'
                    label='Email'
                    fullWidth
                    value={nullStringCheck(addFormState.email)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='phone'
                    label='Phone'
                    fullWidth
                    value={nullStringCheck(addFormState.phone)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='address'
                    label='Address'
                    fullWidth
                    value={nullStringCheck(addFormState.address)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <Typography>Role:</Typography>
                </Grid>
                <Grid item>
                  <Select
                    name='role'
                    value={addFormState.role}
                    fullWidth
                    onChange={handler}
                  >
                    <MenuItem value='1'>Admin</MenuItem>
                    <MenuItem value='2'>User</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Grid>
          )}
          handlerMiddleware={userMiddleware}
          itemValidator={userValidatorFromScratch}
          formInstructions='Name, Email, Company, and Role are required'
          itemName='User'
        />
      )}
      <DeleteItemDialog
        deleteModalOpen={deleteModalOpen}
        setDeleteModalOpen={setDeleteModalOpen}
        itemDeleter={deleteUser}
        setSelectedItems={setSelectedUsers}
        itemsToDelete={selectedUsers}
        itemName='User'
      />
    </>
  );
};

export default UsersOptionsBar;
